import { Chip, Tooltip } from "@heroui/react";
import {
  IconSword,
  IconFlare,
  IconSwords,
  IconCrown,
  IconFlag,
  IconTrophy,
  IconLaurelWreathFilled,
  IconWifi,
} from "@tabler/icons-react";
import { PiTreasureChestFill } from "react-icons/pi";

interface EventTypeChipProps {
  eventType: string;
}

export default function EventTypeChip({ eventType }: EventTypeChipProps) {
  let icon = <IconSwords size={18} />;
  let text = eventType;
  let color = "default";
  let textColor = "";
  let content = undefined;
  let backgroundColor = "";
  let endIcon = null;

  // Default to Standard Battle if eventType is null or undefined
  if (!eventType || eventType === "Standard Battle") {
    icon = <IconSword color={"rgb(148, 163, 184)"} size={18} />;
    content = "Standard Battle. Store level event usually 1-32 players";
    text = "SB";
    textColor = "text-slate-400";
  } else if (eventType === "Extra Grand Battle") {
    icon = <IconSword color={"rgb(148, 163, 184)"} size={18} />;
    content =
      "Extra Grand Battle. Store level event usually 1-32 players. All cards released to date are legal, except for a limited number of banned cards.";
    text = "EGB";
    textColor = "text-slate-400";
  } else if (eventType === "Rare Battle") {
    icon = <IconFlare color={"white"} size={18} />;
    content =
      "Rare Battle. Store level event usually 1-32 players. Can only use cards from one set e.g. only JJK decks at a JJK rare battle";
    text = "RB";
    textColor = "text-white";
    backgroundColor =
      "bg-gradient-to-r from-[rgb(217,0,128)] to-[rgb(38,31,136)]";
  } else if (eventType === "Large Rare Battle") {
    icon = <IconFlare color={"white"} size={18} />;
    content =
      "Large Rare Battle. Regional level event usually 32+ players. Can only use cards from one set e.g. only JJK decks at a JJK rare battle";
    text = "LRB";
    textColor = "text-white";
    backgroundColor =
      "bg-gradient-to-r from-[rgb(217,0,128)] to-[rgb(38,31,136)]";
  } else if (["CS", "CS 3on3", "CS 5on5"].includes(eventType)) {
    content = "Championship Series. Regional level event usually 500+ players";
    color = "warning";
    icon = <IconCrown size={18} />;
  } else if (["GAO"].includes(eventType)) {
    content = "Grand Asia Open. Regional level event usually 500+ players";
    color = "warning";
    icon = <IconCrown size={18} />;
  } else if (
    [
      "Regional",
      "Regional Offline",
      "Regional Online",
      "Online Regional",
      "Offline Regional",
    ].includes(eventType)
  ) {
    content = "Regional. Regional level event usually 500+ players";
    color = "warning";
    text = "RG";
    icon = <IconCrown size={18} />;
    if (eventType === "Regional Online" || eventType === "Online Regional") {
      content =
        "Online Regional. Played virtually at home. Regional level event usually 500+ players";
      endIcon = <IconWifi size={18} />;
    }
  } else if (["Store Treasure Cup"].includes(eventType)) {
    content = "Store Treasure Cup. Store TC event usually 1-32 players";
    color = "primary";
    icon = <PiTreasureChestFill size={18} />;
    text = "STC";
  } else if (["Treasure Cup", "Online Treasure Cup"].includes(eventType)) {
    content = "Treasure Cup. Treasure Cup level event usually 500+ players";
    color = "warning";
    text = "TC";
    icon = <PiTreasureChestFill size={18} />;
    if (eventType === "Online Treasure Cup") {
      content =
        "Online Treasure Cup. Played virtually at home. Treasure Cup level event usually 500+ players";
      endIcon = <IconWifi size={18} />;
    }
  } else if (eventType === "Cash Tournament") {
    content = "Cash Tournament. Unofficial Tournament with cash prizes";
    color = "default";
    textColor = "text-[#86efac]";
    icon = <IconLaurelWreathFilled className={textColor} size={18} />;
    text = "CT";
    backgroundColor = "bg-[#14532d]";
  } else if (eventType.includes("Store Qualifier")) {
    content =
      "Store Qualifier. Store level event usually 32-64 players. Grants top players an invitation to CS events";
    color = "default";
    textColor = "text-[#B794F4]";
    icon = <IconLaurelWreathFilled className={textColor} size={18} />;
    text = "SQ";
    backgroundColor = "bg-[#44337A]";
  } else if (eventType.includes("Store Regional")) {
    content =
      "Store Regional. Store level event usually 32-64 players. Grants top players an invitation to National events";
    color = "default";
    textColor = "text-[#B794F4]";
    icon = <IconLaurelWreathFilled className={textColor} size={18} />;
    text = "SR";
    backgroundColor = "bg-[#44337A]";
  } else if (eventType === "Flagship EX") {
    content = "Flagship EX. Regional level event usually 128-256 players";
    color = "primary";
    text = "FS EX";
    icon = <IconFlag size={18} />;
  } else if (eventType === "Unofficial Tournament") {
    content =
      "Unofficial Tournament. Store level event usually 32-64 players. More competitive than Flagships.";
    text = "UT";
    color = "default";
    icon = <IconTrophy color={"rgb(203, 213, 225)"} size={18} />;
    textColor = "text-slate-300";
  } else if (eventType === "Flagship") {
    content = "Flagship. Store level event usually 32-64 players";
    textColor = "text-[#86BFE0]";
    icon = <IconFlag className={textColor} size={18} />;
    text = "FS";
    backgroundColor = "bg-[#123A63]";
  } else if (eventType === "3on3") {
    content =
      "3on3. Game mode where a team of 3 players plays another team of 3 players";
    color = "default";
    icon = <IconFlag color={"rgb(203, 213, 225)"} size={18} />;
    textColor = "text-slate-300";
  } else if (eventType === "LGS") {
    content = "Local Game Store. Store level event usually 1-32 players";
    color = "default";
    icon = <IconSwords color={"rgb(203, 213, 225)"} size={18} />;
    textColor = "text-slate-300";
  } else if (eventType === "Masters League") {
    content = "Masters League. Regional level event";
    color = "warning";
    icon = <IconCrown size={18} />;
    text = "ML";
  } else if (eventType === "Online Tournament") {
    content = "Online Tournament";
    color = "default";
    icon = <IconWifi color={"rgb(148, 163, 184)"} size={18} />;
    text = "OT";
    textColor = "text-slate-400";
  } else {
    color = "default";
    icon = <IconSwords color={"rgb(203, 213, 225)"} size={18} />;
    textColor = "text-slate-300";
  }

  return (
    <Tooltip
      content={content && <div className="max-w-[200px]">{content}</div>}
    >
      <Chip
        variant="flat"
        color={color as any}
        classNames={{
          base: `${backgroundColor}`,
          content: `${textColor}`,
        }}
        startContent={<div className="ml-1">{icon}</div>}
      >
        <div className="flex items-center">
          <span>{text}</span>{" "}
          {endIcon && <div className="-mr-1">{endIcon}</div>}
        </div>
      </Chip>
    </Tooltip>
  );
}
